import { FC } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import { Grid, Box, ButtonGroup, Button, Divider, MenuItem, FormHelperText } from '@mui/material';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  getAuthorityOptions,
  getCooktopOptions,
  getDrainageOptions,
  getFirePlaceOptions,
  getSepticOptions,
  getWaterHeaterOptions,
  putUtilityMunicipalInfo
} from '../../../../fetch';
import { IAuthorities, IDropdownResponse, IUtilityMunicipalInfo } from '../../../../models';
import { Loader, SMBSelect } from '../../../../components';
import { useQuery } from 'react-query';

interface IUtilityMunicipalityInfo {
  utilityMunicipalityInfo: IUtilityMunicipalInfo;
  isLoadingUtilityMunicipalityInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchUtilityMunicipalityInfo: () => void;
}

const UtilityMuncipalitySchema = Yup.object().shape({
  powerAuthorityId: Yup.number(),
  waterAuthorityId: Yup.number(),
  sewerAuthorityId: Yup.number(),
  gasAuthorityId: Yup.number(),
  hasGas: Yup.boolean(),
  cooktopType: Yup.string(),
  firePlaceType: Yup.string().nullable(),
  waterHeaterType: Yup.string().nullable(),
  drainageType: Yup.string().nullable(),
  septicType: Yup.string()
});

export const UtilityMunicipalityInfo: FC<IUtilityMunicipalityInfo> = ({
  utilityMunicipalityInfo,
  isLoadingUtilityMunicipalityInfo,
  setDirtyForm,
  refetchUtilityMunicipalityInfo
}) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { jobId }: { jobId: string } = useParams();

  // Authorities
  const { isLoading: isLoadingAuthorities, data: Authorities } = useQuery<IAuthorities, Error>(
    ['Authorities'],
    () => getAuthorityOptions(),
    {
      onSuccess: data => {},
      notifyOnChangeProps: 'tracked'
    }
  );

  // Drainage Types
  const { isLoading: isLoadingDrainageTypeOptions, data: drainageTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(['drainageTypes'], () => getDrainageOptions(), {
    onSuccess: data => {},
    notifyOnChangeProps: 'tracked'
  });

  // Septic Types
  const { isLoading: isLoadingSepticTypeOptions, data: SepticTypeOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['septicTypes'], () => getSepticOptions(), {
    onSuccess: data => {},
    notifyOnChangeProps: 'tracked'
  });

  // Water Heater Types
  const { isLoading: isLoadingWaterHeaterTypeOptions, data: waterHeaterOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['waterHeaterTypes'], () => getWaterHeaterOptions(), {
    onSuccess: data => {},
    notifyOnChangeProps: 'tracked'
  });

  // Fire Place Types
  const { isLoading: isLoadingFirePlaceTypeOptions, data: firePlaceOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['firePlaceTypes'], () => getFirePlaceOptions(), {
    onSuccess: data => {},
    notifyOnChangeProps: 'tracked'
  });

  // Cooktop Types
  const { isLoading: isLoadingCooktopTypeOptions, data: CooktopOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['cooktopTypes'], () => getCooktopOptions(), {
    onSuccess: data => {},
    notifyOnChangeProps: 'tracked'
  });

  return (
    <>
      {isLoadingUtilityMunicipalityInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingUtilityMunicipalityInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...utilityMunicipalityInfo,
            powerAuthorityId: utilityMunicipalityInfo?.powerAuthorityId ?? null,
            waterAuthorityId: utilityMunicipalityInfo?.waterAuthorityId ?? null,
            sewerAuthorityId: utilityMunicipalityInfo?.sewerAuthorityId ?? null,
            gasAuthorityId: utilityMunicipalityInfo?.gasAuthorityId ?? null,
            hasGas: utilityMunicipalityInfo?.hasGas ?? false,
            cookTopType: utilityMunicipalityInfo?.cookTopType ?? null,
            firePlaceType: utilityMunicipalityInfo?.firePlaceType ?? null,
            waterHeaterType: utilityMunicipalityInfo?.waterHeaterType ?? null,
            septicType: utilityMunicipalityInfo?.septicType ?? SepticTypeOptions?.[0]?.value ?? '',
            drainageType: utilityMunicipalityInfo?.drainageType ?? drainageTypes?.[0]?.value ?? ''
          }}
          validationSchema={UtilityMuncipalitySchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IUtilityMunicipalInfo = {
                ...values
              };

              await putUtilityMunicipalInfo(parseInt(jobId), data);
              refetchUtilityMunicipalityInfo();
              enqueueSnackbar('Utility / Muncipality information has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(
                error?.Detail ??
                  'Something went wrong. Utility / Muncipality information was not updated.',
                {
                  variant: 'error'
                }
              );
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              name="PowerCompany"
                              label="Power Company"
                              id="powerAuthorityId"
                              variant="outlined"
                              disabled={isLoadingAuthorities}
                              value={values.powerAuthorityId}
                              onChange={({ target: { value } }) => {
                                setFieldValue('powerAuthorityId', value);
                              }}
                              onBlur={handleBlur}
                              error={errors?.powerAuthorityId ? true : false}
                            >
                              {Authorities?.power &&
                                Authorities?.power.map((authority, index) => {
                                  return (
                                    <MenuItem key={`${authority.value}`} value={authority.value}>
                                      {authority.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched?.powerAuthorityId && errors?.powerAuthorityId && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              name="Water Company"
                              label="Water Company"
                              id="waterAuthorityId"
                              variant="outlined"
                              disabled={isLoadingAuthorities}
                              value={values.waterAuthorityId}
                              onChange={({ target: { value } }) => {
                                setFieldValue('waterAuthorityId', value);
                              }}
                              onBlur={handleBlur}
                              error={errors?.waterAuthorityId ? true : false}
                            >
                              {Authorities?.water &&
                                Authorities?.water.map((authority, index) => {
                                  return (
                                    <MenuItem key={`${authority.value}`} value={authority.value}>
                                      {authority.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched?.waterAuthorityId && errors?.waterAuthorityId && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <SMBSelect
                                  name="sewageTreatmentMethod"
                                  label="Sewage Treatment Method"
                                  id="sewageTreatmentMethod"
                                  variant="outlined"
                                  disabled={isLoadingDrainageTypeOptions}
                                  value={values.drainageType}
                                  onChange={({ target: { value } }) => {
                                    setFieldValue('drainageType', value);
                                  }}
                                  onBlur={handleBlur}
                                  error={errors?.drainageType ? true : false}
                                >
                                  {drainageTypes?.map((drainageType, index) => {
                                    return (
                                      <MenuItem
                                        key={`${drainageType.value}`}
                                        value={drainageType.value}
                                      >
                                        {drainageType.description}
                                      </MenuItem>
                                    );
                                  })}
                                </SMBSelect>
                                {touched?.drainageType && errors?.drainageType && (
                                  <FormHelperText error>Required</FormHelperText>
                                )}
                              </Grid>
                              {values.drainageType === 'Sewer' && (
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    name="Sewer Provider"
                                    label="Sewer Provider"
                                    id="sewerAuthorityId"
                                    variant="outlined"
                                    disabled={isLoadingAuthorities}
                                    value={values.sewerAuthorityId}
                                    onChange={({ target: { value } }) => {
                                      setFieldValue('sewerAuthorityId', value);
                                    }}
                                    onBlur={handleBlur}
                                    error={errors?.sewerAuthorityId ? true : false}
                                  >
                                    {Authorities?.sewer.map((authority, index) => {
                                      return (
                                        <MenuItem
                                          key={`${authority.value}`}
                                          value={authority.value}
                                        >
                                          {authority.description}
                                        </MenuItem>
                                      );
                                    })}
                                  </SMBSelect>
                                  {touched?.sewerAuthorityId && errors?.sewerAuthorityId && (
                                    <FormHelperText error>Required</FormHelperText>
                                  )}
                                </Grid>
                              )}
                              {values.drainageType === 'Septic' && (
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    name="Septic Type"
                                    label="Septic Type"
                                    id="septicType"
                                    variant="outlined"
                                    disabled={isLoadingSepticTypeOptions}
                                    value={values.septicType}
                                    onChange={({ target: { value } }) => {
                                      setFieldValue('septicType', value);
                                    }}
                                    onBlur={handleBlur}
                                    error={errors?.septicType ? true : false}
                                  >
                                    {SepticTypeOptions?.map((types, index) => {
                                      return (
                                        <MenuItem key={`${types.value}`} value={types.value}>
                                          {types.description}
                                        </MenuItem>
                                      );
                                    })}
                                  </SMBSelect>
                                  {touched?.septicType && errors?.septicType && (
                                    <FormHelperText error>Required</FormHelperText>
                                  )}
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <SMBSelect
                                  name="gas"
                                  label="Gas"
                                  id="hasGas"
                                  variant="outlined"
                                  disabled={false}
                                  value={values.hasGas}
                                  onChange={e => {
                                    const hasGas = e.target.value === 'true';
                                    setFieldValue('hasGas', hasGas);
                                    if (values.waterHeaterType === 'NaturalGas' && !hasGas) {
                                      if (values.waterHeaterType === 'NaturalGas') {
                                        setFieldValue('waterHeaterType', null);
                                      }
                                      if (values.firePlaceType === 'NaturalGas') {
                                        setFieldValue('firePlaceType', null);
                                      }
                                      if (values.cookTopType === 'NaturalGas') {
                                        setFieldValue('cookTopType', null);
                                      }
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  error={errors?.hasGas ? true : false}
                                >
                                  {[
                                    { value: 'true', shorthand: 'Yes', description: 'Yes' },
                                    { value: 'false', shorthand: 'No', description: 'No' }
                                  ].map((hasGas, index) => {
                                    return (
                                      <MenuItem key={`${hasGas.value}`} value={hasGas.value}>
                                        {hasGas.description}
                                      </MenuItem>
                                    );
                                  })}
                                </SMBSelect>
                                {touched?.hasGas && errors?.hasGas && (
                                  <FormHelperText error>Required</FormHelperText>
                                )}
                              </Grid>

                              {values.hasGas && (
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    name="Gas Provider"
                                    label="Gas Provider"
                                    id="gasAuthorityId"
                                    variant="outlined"
                                    disabled={isLoadingAuthorities}
                                    value={values.gasAuthorityId}
                                    onChange={({ target: { value } }) => {
                                      setFieldValue('gasAuthorityId', value);
                                    }}
                                    onBlur={handleBlur}
                                    error={errors?.gasAuthorityId ? true : false}
                                  >
                                    {Authorities?.gas.map((authority, index) => {
                                      return (
                                        <MenuItem
                                          key={`${authority.value}`}
                                          value={authority.value}
                                        >
                                          {authority.description}
                                        </MenuItem>
                                      );
                                    })}
                                  </SMBSelect>
                                  {touched?.gasAuthorityId && errors?.gasAuthorityId && (
                                    <FormHelperText error>Required</FormHelperText>
                                  )}
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <SMBSelect
                                  name="Water Heater"
                                  label="Water Heater"
                                  id="waterHeaterType"
                                  variant="outlined"
                                  disabled={isLoadingWaterHeaterTypeOptions}
                                  value={values.waterHeaterType}
                                  onChange={({ target: { value } }) => {
                                    setFieldValue('waterHeaterType', value);
                                  }}
                                  handleClear={() => setFieldValue('waterHeaterType', null)}
                                  onBlur={handleBlur}
                                  error={errors?.waterHeaterType ? true : false}
                                >
                                  {waterHeaterOptions?.map((option, index) => {
                                    return (
                                      <MenuItem
                                        disabled={option.value === 'NaturalGas' && !values.hasGas}
                                        key={`${option.value}`}
                                        value={option.value}
                                      >
                                        {option.description}
                                      </MenuItem>
                                    );
                                  })}
                                </SMBSelect>
                                {touched?.waterHeaterType && errors?.waterHeaterType && (
                                  <FormHelperText error>Required</FormHelperText>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <SMBSelect
                                  name="Fireplace"
                                  label="Fireplace"
                                  id="firePlaceType"
                                  variant="outlined"
                                  disabled={isLoadingFirePlaceTypeOptions}
                                  value={values.firePlaceType}
                                  onChange={({ target: { value } }) => {
                                    setFieldValue('firePlaceType', value);
                                  }}
                                  handleClear={() => setFieldValue('firePlaceType', null)}
                                  onBlur={handleBlur}
                                  error={errors?.firePlaceType ? true : false}
                                >
                                  {firePlaceOptions?.map((option, index) => {
                                    return (
                                      <MenuItem
                                        disabled={option.value === 'NaturalGas' && !values.hasGas}
                                        key={`${option.value}`}
                                        value={option.value}
                                      >
                                        {option.description}
                                      </MenuItem>
                                    );
                                  })}
                                </SMBSelect>
                                {touched?.firePlaceType && errors?.firePlaceType && (
                                  <FormHelperText error>Required</FormHelperText>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <SMBSelect
                                  name="Cooktop"
                                  label="Cooktop"
                                  id="cooktopType"
                                  variant="outlined"
                                  disabled={isLoadingCooktopTypeOptions}
                                  value={values.cookTopType}
                                  onChange={({ target: { value } }) => {
                                    setFieldValue('cookTopType', value);
                                  }}
                                  handleClear={() => setFieldValue('cookTopType', null)}
                                  onBlur={handleBlur}
                                  error={errors?.cookTopType ? true : false}
                                >
                                  {CooktopOptions?.map((option, index) => {
                                    return (
                                      <MenuItem
                                        disabled={option.value === 'NaturalGas' && !values.hasGas}
                                        key={`${option.value}`}
                                        value={option.value}
                                      >
                                        {option.description}
                                      </MenuItem>
                                    );
                                  })}
                                </SMBSelect>
                                {touched?.cookTopType && errors?.cookTopType && (
                                  <FormHelperText error>Required</FormHelperText>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={jobId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/jobs');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};
